<template>
  <div class="user-dashboard">
    <!-- Dashboard Style Topbar Header -->
    <div class="dashboard-sticky-area">
        <div class="dashboard-topbar">
        <h4 class="dashboard-page-title">
          <CIcon name="cil-speedometer" class="dashboard-title-icon" />
          <span v-html="$t('mainuser.pageTitle')"></span>
        </h4>

        <div class="dashboard-header-right">
          <button class="report-btn hero-action" @click="$router.push('/supervisor/new-request')">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            <span>{{ $t('mainuser.createNewRequest') }}</span>
          </button>
        </div>
      </div>
    </div>

      <HomeStatCards :cards="statCards" :active-key="selectedStatus" @select="selectedStatus = $event" />

      <CCard class="details-card mb-4">
        <CCardHeader class="details-card-header"><h4 class="mb-0">{{ activeTableTitle }}</h4></CCardHeader>
        <CCardBody>
          <Ureportable
            ref="activeReportTable"
            mode="unfinished"
            ticket-source="supervisor"
            :status-filter="selectedStatus"
            :date-range="selectedRange"
            :start-date="startDate"
            :end-date="endDate"
            :search-query="searchQuery"
            @tickets-loaded="updateStats"
            @show-detail="handleShowDetail"
            @forward-ticket="openForwardModal"
          />
        </CCardBody>
      </CCard>

      <!-- Forward Ticket Modal on Supervisor Home -->
      <CModal :show.sync="showForwardModal" centered :title="$t('ticketDetail.supervisor.forwardOther') || 'ส่งต่อหน่วยงานอื่น'">
        <div v-if="forwardingTicket" class="p-2">
          <div class="mb-3 p-3 bg-light rounded border">
            <div class="font-weight-bold text-primary mb-1">
              #{{ forwardingTicket.ticketNumber }}
            </div>
            <div class="text-dark small">
              {{ forwardingTicket.userPart && forwardingTicket.userPart.problemDetails ? forwardingTicket.userPart.problemDetails : '-' }}
            </div>
          </div>

          <div class="form-group mb-3">
            <label class="font-weight-bold text-dark mb-1">
              {{ $t('ticketDetail.supervisor.pickTargetDept') || 'เลือกหน่วยงานปลายทางที่ต้องการส่งต่อ' }}
            </label>
            <select class="form-control" v-model="selectedForwardCategoryId">
              <option :value="null" disabled>{{ $t('ticketDetail.supervisor.placeholderDept') || 'กรุณาเลือกหน่วยงาน' }}</option>
              <option
                v-for="cat in allCategories.filter(Boolean)"
                :key="cat._id"
                :value="cat._id"
                :disabled="isCurrentCategory(cat._id)"
              >
                {{ cat.departmentName }}
                <span v-if="isCurrentCategory(cat._id)">({{ $t('ticketDetail.supervisor.yourDeptTag') || 'หน่วยงานปัจจุบัน' }})</span>
              </option>
            </select>
          </div>
        </div>
        <template #footer>
          <CButton
            color="primary"
            class="font-weight-bold px-4"
            style="background-color: #1e40af; border-color: #1e40af;"
            @click="submitForwardTicket"
            :disabled="!selectedForwardCategoryId || isForwarding"
          >
            {{ isForwarding ? ($t('ticketDetail.supervisor.processing') || 'กำลังดำเนินการ...') : ($t('ticketDetail.supervisor.confirmForward') || 'ยืนยันการส่งต่อ') }}
          </CButton>
          <CButton color="danger" variant="outline" @click="closeForwardModal" :disabled="isForwarding">
            {{ $t('mgmt.cancel') || 'ยกเลิก' }}
          </CButton>
        </template>
      </CModal>

      <TicketDetailModal v-if="selectedReport" @close="selectedReport=null"><ReportDetail :report="selectedReport" :is-reporter="selectedReport._role === 'reporter'" @close="selectedReport=null" @refresh="refreshTables" @update:report="selectedReport = $event" /></TicketDetailModal>
  </div>
</template>

<script>
import CModal from '@/components/common/AccessibleModal.vue';
import HomeStatCards from '@/components/common/HomeStatCards.vue'
import Ureportable from '@/components/user/Ureportable.vue'
import ReportDetail from './ReportDetail.vue'
import TicketDetailModal from '@/components/common/TicketDetailModal.vue'
import Service from '@/service/api'
import Swal from 'sweetalert2'

export default {
  name: 'SupervisorHome',
  components: { CModal, HomeStatCards, Ureportable, ReportDetail, TicketDetailModal },
  data: () => ({
    selectedReport: null,
    selectedStatus: 'new',
    selectedRange: 'all',
    startDate: '',
    endDate: '',
    searchQuery: '',
    showSearchDropdown: false,
    currentMatchIndex: 0,
    loadedTickets: [],
    showForwardModal: false,
    forwardingTicket: null,
    allCategories: [],
    selectedForwardCategoryId: null,
    isForwarding: false,
    stats: {
      newJobs: 0,
      inProgress: 0,
      completed: 0,
      overdue: 0,
    },
  }),
  computed: {
    rangeOptions() {
      return [
        { label: this.dashboardText("สัปดาห์", "Week"), value: "week" },
        { label: this.dashboardText("เดือน", "Month"), value: "month" },
        { label: this.dashboardText("ปี", "Year"), value: "year" },
        { label: "", value: "custom" },
      ];
    },
    searchMatches() {
      if (!this.searchQuery || !this.searchQuery.trim()) return [];
      const q = this.searchQuery.trim().toLowerCase();
      const results = [];
      this.loadedTickets.forEach((ticket) => {
        const ticketNo = String(ticket.ticketNumber || ticket._id || "").toLowerCase();
        const reporter = String(ticket.userPart?.name || ticket.userPart?.username || "").toLowerCase();
        const prob = String(ticket.userPart?.problemDetails || "").toLowerCase();
        const dept = String(ticket.userPart?.category?.departmentName || ticket.userPart?.category?.name || ticket.userPart?.category || "").toLowerCase();
        const cat = String(ticket.userPart?.subCategory?.name || ticket.userPart?.subCategory || "").toLowerCase();
        const status = String(ticket.status || "").toLowerCase();

        if (ticketNo.includes(q) || reporter.includes(q) || prob.includes(q) || dept.includes(q) || cat.includes(q) || status.includes(q)) {
          results.push({
            id: 'ticket-' + (ticket._id || ticket.ticketNumber),
            ticket,
            sectionLabel: this.dashboardText('ใบแจ้งซ่อม', 'Ticket'),
            badgeClass: 'badge-ticket',
            title: `#${ticket.ticketNumber || '-'} — ${ticket.userPart?.name || this.dashboardText('ผู้แจ้ง', 'Reporter')}`,
            description: ticket.userPart?.problemDetails || '-'
          });
        }
      });
      return results;
    },
    activeTableTitle() {
      const isEn = (this.$i18n?.locale || this.$store?.state?.locale?.lang || '').toLowerCase().startsWith('en');
      const baseTitle = this.$t('mainuser.activeRequestLabel') || (isEn ? 'Maintenance Requests' : 'รายการคำขอแจ้งซ่อม');
      const suffixMap = {
        new: isEn ? 'New Requests' : 'งานที่เข้าใหม่',
        active: isEn ? 'In Progress' : 'กำลังซ่อมทั้งหมด',
        overdue: isEn ? 'Overdue SLA' : 'งานที่เกินกำหนด SLA',
        completed: isEn ? 'Completed' : 'งานที่ซ่อมเสร็จ',
        all: isEn ? 'All' : 'ทั้งหมด',
      };
      const suffix = suffixMap[this.selectedStatus];
      return suffix ? `${baseTitle} — ${suffix}` : baseTitle;
    },
    statCards() {
      return [
        {
          key: "new",
          title: this.$t("home.kpiNewJobs") || "งานที่เข้าใหม่",
          value: this.stats.newJobs,
          icon: "cil-bell",
          iconClass: "icon-blue",
          badge: "NEW",
          badgeClass: "trend-neutral",
        },
        {
          key: "active",
          title: this.$t("home.kpiInProgressAll") || "กำลังซ่อมทั้งหมด",
          value: this.stats.inProgress,
          icon: "cil-av-timer",
          iconClass: "icon-orange",
          badge: "ACTIVE",
          badgeClass: "trend-orange",
        },
        {
          key: "overdue",
          title: this.dashboardText('งานที่เกินกำหนด SLA', 'Overdue SLA Jobs'),
          value: this.stats.overdue,
          icon: "cil-clock",
          iconClass: "icon-red",
          badge: "OVER SLA",
          badgeClass: this.stats.overdue > 0 ? "trend-down" : "trend-up",
        },
        {
          key: "completed",
          title: this.$t("home.kpiCompletedJobs") || "งานที่ซ่อมเสร็จ",
          value: this.stats.completed,
          icon: "cil-check-circle",
          iconClass: "icon-green",
          badge: "DONE",
          badgeClass: "trend-up",
        },
      ];
    },
  },
  watch: {
    '$route.query.status'(status) {
      if (status) this.selectedStatus = status;
    },
    '$route.query.filter'(filter) {
      if (filter) this.selectedStatus = filter;
    },
    '$route.query': {
      handler(query) {
        if (query && (query.ticketId || query.ticketNo)) {
          this.selectedRange = 'all';
          this.$nextTick(() => {
            this.$refs.activeReportTable?.jumpToTicket(query.ticketId, query.ticketNo);
          });
        }
      }
    },
  },
  mounted() {
    if (this.$route.query.status) {
      this.selectedStatus = this.$route.query.status;
    }
    if (this.$route.query.filter) {
      this.selectedStatus = this.$route.query.filter;
    }
    const queryTicket = this.$route.query.ticketId || this.$route.query.ticketNo || this.$route.query.ticket;
    if (queryTicket) {
      this.selectedRange = 'all';
      this.$nextTick(() => {
        this.$refs.activeReportTable?.jumpToTicket(this.$route.query.ticketId || queryTicket, this.$route.query.ticketNo || queryTicket);
      });
    }
    window.addEventListener('keydown', this.handleGlobalKeydown);
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handleGlobalKeydown);
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    dashboardText(thaiText, englishText) {
      const locale = String(this.$i18n?.locale || this.$store?.state?.locale?.lang || 'th').toLowerCase();
      return locale.startsWith('th') ? thaiText : englishText;
    },

    selectRange(range) {
      this.selectedRange = range;
      if (range !== 'custom') {
        this.startDate = '';
        this.endDate = '';
      }
    },
    onCustomDateChange() {
      if (this.startDate && this.endDate) {
        this.selectedRange = 'custom';
      }
    },
    nextMatch() {
      if (!this.searchMatches.length) return;
      this.currentMatchIndex = (this.currentMatchIndex + 1) % this.searchMatches.length;
      this.jumpToMatch(this.currentMatchIndex);
    },
    prevMatch() {
      if (!this.searchMatches.length) return;
      this.currentMatchIndex = (this.currentMatchIndex - 1 + this.searchMatches.length) % this.searchMatches.length;
      this.jumpToMatch(this.currentMatchIndex);
    },
    jumpToMatch(idx) {
      if (!this.searchMatches[idx]) return;
      this.currentMatchIndex = idx;
      const match = this.searchMatches[idx];
      this.showSearchDropdown = false;
      if (match.ticket) {
        this.handleShowDetail(match.ticket);
      }
    },
    clearSearch() {
      this.searchQuery = '';
      this.showSearchDropdown = false;
      this.currentMatchIndex = 0;
    },
    closeSearchDropdown() {
      this.showSearchDropdown = false;
    },
    handleGlobalKeydown(e) {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'f') {
        const input = this.$refs.searchBoxRef?.querySelector('input');
        if (input) {
          e.preventDefault();
          input.focus();
          input.select();
          this.showSearchDropdown = true;
        }
      }
    },
    handleClickOutside(e) {
      if (this.$refs.searchBoxRef && !this.$refs.searchBoxRef.contains(e.target)) {
        this.showSearchDropdown = false;
      }
    },

    async findAndOpenTicket(idOrNumber) {
      if (!idOrNumber) return;
      const target = (idOrNumber + '').trim().toLowerCase();
      const loaded = this.$refs.activeReportTable?.unfinishedTickets || [];
      const found = loaded.find(t =>
        (t._id && t._id.toLowerCase() === target) ||
        (t.ticketNumber && t.ticketNumber.toLowerCase() === target) ||
        (t.ticketNo && t.ticketNo.toLowerCase() === target)
      );
      if (found) {
        this.handleShowDetail(found);
        return;
      }
      try {
        const res = await Service.ticket('get', null, { urlAppend: `/${idOrNumber}` });
        if (res?.data?.success && res.data.data) {
          this.handleShowDetail(res.data.data);
        }
      } catch (err) {
        console.warn('Ticket not found in Home:', idOrNumber);
      }
    },
    updateStats(tickets) {
      const source = Array.isArray(tickets) ? tickets : [];
      this.loadedTickets = source;
      const newJobs = source.filter(ticket => ["pending", "assigned"].includes(ticket.status)).length;
      const inProgress = source.filter(ticket => ticket.status === "in_progress").length;
      const completed = source.filter(ticket => ["done", "welldone"].includes(ticket.status)).length;
      const overdue = source.filter(ticket => {
        if (!ticket.slaDeadline) return false;
        const end = ["done", "welldone"].includes(ticket.status)
          ? new Date(ticket.technicianPart?.dateTimeFinished || ticket.updatedAt)
          : new Date();
        return end.getTime() > new Date(ticket.slaDeadline).getTime();
      }).length;
      if (this.selectedReport && this.selectedReport._id) {
        const matching = source.find(t => String(t._id) === String(this.selectedReport._id));
        if (matching) {
          this.selectedReport = { ...this.selectedReport, ...matching };
        }
      }
      this.stats = { newJobs, inProgress, completed, overdue };

      const queryTicket = this.$route.query.ticketId || this.$route.query.ticketNo || this.$route.query.ticket;
      if (queryTicket) {
        this.$nextTick(() => {
          this.$refs.activeReportTable?.jumpToTicket(this.$route.query.ticketId || queryTicket, this.$route.query.ticketNo || queryTicket);
        });
      }
    },
    handleShowDetail(item) {
      this.selectedReport = { ...item }
      this.$nextTick(() => document.getElementById('supervisor-home-detail')?.scrollIntoView({ behavior: 'smooth', block: 'start' }))
    },
    refreshTables(opts) {
      if (opts && opts.updatedTicket && this.selectedReport) {
        this.selectedReport = { ...this.selectedReport, ...opts.updatedTicket };
      }
      if (!opts || opts.keepOpen !== true) {
        this.selectedReport = null
      }
      this.$refs.activeReportTable?.loadTickets()
    },
    async openForwardModal(ticket) {
      this.forwardingTicket = ticket;
      this.selectedForwardCategoryId = null;
      this.showForwardModal = true;
      if (!this.allCategories.length) {
        await this.fetchAllCategories();
      }
    },
    closeForwardModal() {
      this.showForwardModal = false;
      this.forwardingTicket = null;
      this.selectedForwardCategoryId = null;
      this.isForwarding = false;
    },
    async fetchAllCategories() {
      try {
        const response = await Service.category('get');
        if (response.data.success) {
          this.allCategories = Array.isArray(response.data.data) ? response.data.data : [];
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    },
    isCurrentCategory(catId) {
      const ticketCatId = this.forwardingTicket?.userPart?.category?._id
        || this.forwardingTicket?.userPart?.category
        || this.forwardingTicket?.category;
      return String(ticketCatId) === String(catId);
    },
    async submitForwardTicket() {
      if (!this.selectedForwardCategoryId || !this.forwardingTicket) return;
      const targetCategory = this.allCategories.find(c => c && c._id === this.selectedForwardCategoryId);
      const targetName = targetCategory?.departmentName || this.dashboardText('หน่วยงานปลายทาง', 'Target Department');

      const result = await Swal.fire({
        title: this.$t('ticketDetail.supervisor.forwardConfirmTitle') || 'ยืนยันการส่งต่องาน',
        text: (this.$t('ticketDetail.supervisor.forwardConfirmText') || 'คุณต้องการส่งต่องานนี้ไปยัง {dept} ใช่หรือไม่?').replace('{dept}', targetName),
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#1e40af',
        cancelButtonColor: '#dc2626',
        confirmButtonText: this.$t('mgmt.confirm') || 'ยืนยัน',
        cancelButtonText: this.$t('mgmt.cancel') || 'ยกเลิก'
      });

      if (result.isConfirmed) {
        this.isForwarding = true;
        try {
          const response = await Service.ticket('patch', {
            newCategoryId: this.selectedForwardCategoryId
          }, { urlAppend: `/${this.forwardingTicket._id}/forward` });

          if (response.data.success) {
            Swal.fire({
              title: this.$t('mgmt.success') || 'สำเร็จ',
              text: this.$t('ticketDetail.supervisor.forwardOk') || 'ส่งต่องานเรียบร้อยแล้ว',
              icon: 'success'
            });
            this.closeForwardModal();
            this.refreshTables();
          } else {
            throw new Error(response.data.message || 'Forward failed');
          }
        } catch (error) {
          console.error(error);
          Swal.fire(
            this.$t('ticketDetail.swalError') || 'เกิดข้อผิดพลาด',
            error.response?.data?.message || this.$t('ticketDetail.supervisor.forwardFail') || 'ไม่สามารถส่งต่องานได้',
            'error'
          );
        } finally {
          this.isForwarding = false;
        }
      }
    },
  },
}
</script>

<style scoped>
.user-dashboard {
  margin-top: 0 !important;
  padding-top: 0 !important;
}

.dashboard-sticky-area {
  position: sticky !important;
  top: 76px !important;
  z-index: 1020 !important;
  margin: 0 0 20px 0 !important;
  padding: 0 0 6px 0 !important;
  width: 100%;
  background: #ebedef !important;
}

.dashboard-topbar {
  width: 100%;
  min-width: 0;
  height: 112px !important;
  min-height: 112px !important;
  margin: 0 !important;
  padding: 22px 28px !important;
  display: flex !important;
  flex-direction: row !important;
  align-items: center !important;
  justify-content: space-between !important;
  gap: 16px !important;
  flex-wrap: nowrap !important;
  background: #1e40af !important;
  border: 0 !important;
  border-radius: 12px !important;
  box-sizing: border-box !important;
  color: #fff !important;
}

.dashboard-page-title {
  margin: 0 !important;
  padding: 0 !important;
  display: flex !important;
  align-items: center !important;
  gap: 16px !important;
  color: #ffffff !important;
  font-size: 1.6rem !important;
  font-weight: 800 !important;
  letter-spacing: -0.02em !important;
  line-height: 1.2 !important;
  min-width: 0 !important;
  flex: 1 1 auto !important;
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
}

.dashboard-title-icon,
.dashboard-page-title svg,
::v-deep .dashboard-page-title .c-icon {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: 54px !important;
  height: 54px !important;
  flex: 0 0 54px !important;
  border-radius: 16px !important;
  background: rgba(255, 255, 255, 0.16) !important;
  color: #ffffff !important;
  padding: 11px !important;
  margin: 0 !important;
}

.dashboard-filter {
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-shrink: 0;
  gap: 8px;
}

.report-btn,
.hero-action {
  background: #ffffff !important;
  border: none !important;
  color: #1e40af !important;
  min-height: 48px !important;
  padding: 10px 24px !important;
  border-radius: 50px !important;
  font-size: 15px !important;
  font-weight: 700 !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 8px !important;
  cursor: pointer !important;
  white-space: nowrap !important;
  flex-shrink: 0 !important;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12) !important;
  transition: all 0.2s ease !important;
}

.report-btn:hover,
.hero-action:hover {
  background: #fdfdfd !important;
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2) !important;
}

.details-card {
  border-radius: 16px !important;
  overflow: hidden;
  border: 1px solid rgba(37, 99, 235, 0.12);
  box-shadow: 0 4px 20px rgba(37, 99, 235, 0.06);
}

.details-card-header {
  background: #f8fafc !important;
  border-bottom: 1px solid rgba(37, 99, 235, 0.08) !important;
  color: #1e40af !important;
  border-bottom: 1px solid rgba(37, 99, 235, 0.1);
  padding: 18px 22px;
}

.details-card-header h4 {
  margin-bottom: 0;
  color: #1e40af;
  font-weight: 800;
}

::v-deep .card-btn {
  border-radius: 8px !important;
  box-shadow: none !important;
}

.dashboard-header-right {
  display: flex !important;
  flex-direction: row !important;
  align-items: center !important;
  justify-content: flex-end !important;
  width: auto !important;
  flex-shrink: 0 !important;
  margin-left: auto !important;
}

@media (max-width: 1200px) {
  .dashboard-sticky-area {
    top: 0 !important;
    margin: 0 0 16px 0 !important;
    padding: 4px 0 6px 0 !important;
    background: #ebedef !important;
  }
}

@media (max-width: 768px) {
  .dashboard-sticky-area {
    top: 0 !important;
    margin: 0 0 12px 0 !important;
    padding: 2px 0 6px 0 !important;
    background: #ebedef !important;
  }
  .dashboard-topbar {
    display: flex !important;
    flex-direction: row !important;
    align-items: center !important;
    justify-content: space-between !important;
    flex-wrap: nowrap !important;
    height: auto !important;
    min-height: 88px !important;
    padding: 14px 18px !important;
    border-radius: 12px !important;
    gap: 12px !important;
    margin: 0 !important;
  }
  .dashboard-header-right {
    display: flex !important;
    flex-direction: row !important;
    align-items: center !important;
    justify-content: flex-end !important;
    width: auto !important;
    flex-shrink: 0 !important;
    margin-left: auto !important;
  }
  .dashboard-page-title {
    font-size: 1.3rem !important;
    gap: 12px !important;
  }
  .dashboard-title-icon,
  .dashboard-page-title svg,
  ::v-deep .dashboard-page-title .c-icon {
    width: 46px !important;
    height: 46px !important;
    flex: 0 0 46px !important;
    border-radius: 13px !important;
    padding: 9px !important;
  }
  .report-btn,
  .hero-action {
    min-height: 44px !important;
    padding: 8px 18px !important;
    font-size: 13.5px !important;
    gap: 6px !important;
    white-space: nowrap !important;
    flex-shrink: 0 !important;
  }
  .details-card-header { padding: 14px 16px; }
  .details-card-header h4 { font-size: 1rem; }
  .details-card .card-body { padding: 12px; }
}

@media (max-width: 480px) {
  .dashboard-topbar {
    display: flex !important;
    flex-direction: row !important;
    align-items: center !important;
    justify-content: space-between !important;
    flex-wrap: nowrap !important;
    height: auto !important;
    min-height: 84px !important;
    padding: 12px 16px !important;
    border-radius: 12px !important;
    gap: 10px !important;
    margin: 0 !important;
  }
  .dashboard-header-right {
    display: flex !important;
    flex-direction: row !important;
    align-items: center !important;
    justify-content: flex-end !important;
    width: auto !important;
    flex-shrink: 0 !important;
    margin-left: auto !important;
  }
  .dashboard-page-title {
    font-size: 1.15rem !important;
    gap: 10px !important;
  }
  .dashboard-title-icon,
  .dashboard-page-title svg,
  ::v-deep .dashboard-page-title .c-icon {
    width: 42px !important;
    height: 42px !important;
    flex: 0 0 42px !important;
    border-radius: 12px !important;
    padding: 8px !important;
  }
  .report-btn,
  .hero-action {
    min-height: 40px !important;
    padding: 7px 14px !important;
    font-size: 13px !important;
    gap: 5px !important;
    white-space: nowrap !important;
    flex-shrink: 0 !important;
  }
}
</style>
